import React, { useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import moment from 'moment';
import useWeekCalculations from '../../functions/useWeekCalculations';
import EditJobModal from '../JobSchedulePage/EditJobModal';
import { createModalCloseHandler } from '../JobSchedulePage/utils/modalHandlers';
import {
  buildControlsEngineerOptions,
  filterControlsJobs,
  getCalendarRangeSummary,
  getControlsStatusLabel,
} from './utils/controlsScheduleData';

const RANGE_OPTIONS = [
  { id: '4weeks', label: '4 Weeks', weeks: 4 },
  { id: '8weeks', label: '8 Weeks', weeks: 8 },
  { id: '12weeks', label: '12 Weeks', weeks: 12 },
];


const ControlsCalendar = ({ filteredJobs = [], canEdit = false }) => {
  const controlOptions = useSelector((state) => state.dropdown.userDropdowns.controlOptions || []);
  const { weekColumns } = useWeekCalculations(12);
  const [selectedEngineer, setSelectedEngineer] = useState('all');
  const [showCompleted, setShowCompleted] = useState(true);
  const [activeRange, setActiveRange] = useState('8weeks');
  const [showModal, setShowModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  const activeRangeConfig = useMemo(
    () => RANGE_OPTIONS.find((option) => option.id === activeRange) || RANGE_OPTIONS[1],
    [activeRange]
  );

  const engineerOptions = useMemo(
    () => buildControlsEngineerOptions(controlOptions, filteredJobs),
    [controlOptions, filteredJobs]
  );

  const visibleJobs = useMemo(
    () => filterControlsJobs(filteredJobs, { selectedEngineer, showCompleted }),
    [filteredJobs, selectedEngineer, showCompleted]
  );

  const visibleWeeks = useMemo(
    () => (weekColumns || []).slice(0, activeRangeConfig.weeks),
    [weekColumns, activeRangeConfig.weeks]
  );
  const visibleRangeDates = useMemo(
    () => visibleWeeks.flatMap((week) => {
      const weekStart = moment(week.startDate);
      return Array.from({ length: 7 }, (_, index) => moment(weekStart).add(index, 'days').format('YYYY-MM-DD'));
    }),
    [visibleWeeks]
  );
  const summary = useMemo(
    () => getCalendarRangeSummary(visibleJobs, visibleRangeDates),
    [visibleJobs, visibleRangeDates]
  );

  const jobsByDate = useMemo(() => {
    return visibleJobs.reduce((groups, job) => {
      if (!job?.SoftwareDue) {
        return groups;
      }

      const dateKey = moment(job.SoftwareDue).format('YYYY-MM-DD');
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }

      groups[dateKey].push(job);
      return groups;
    }, {});
  }, [visibleJobs]);

  const unscheduledJobs = useMemo(
    () => visibleJobs.filter((job) => !job?.SoftwareDue),
    [visibleJobs]
  );

  const handleOpenModal = (job) => {
    if (!canEdit) {
      return;
    }

    setSelectedJob(job);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedJob(null);
  };

  const handleSaveChanges = () => {
    console.log('Controls calendar job updated successfully');
  };

  return (
    <div className="controls-page-container">
      <div className="controls-toolbar">
        <Row className="g-3 align-items-end">
          <Col lg={4} md={6}>
            <Form.Group>
              <Form.Label>Controls Engineer</Form.Label>
              <Form.Select value={selectedEngineer} onChange={(event) => setSelectedEngineer(event.target.value)}>
                {engineerOptions.map((engineerName) => (
                  <option key={engineerName} value={engineerName}>
                    {engineerName === 'all' ? 'All Controls Engineers' : engineerName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col lg={4} md={6}>
            <Form.Check
              type="switch"
              id="controls-calendar-show-completed"
              label="Show completed software jobs"
              checked={showCompleted}
              onChange={(event) => setShowCompleted(event.target.checked)}
            />
          </Col>
          <Col lg={4} md={12}>
            <div className="d-flex gap-2 flex-wrap justify-content-lg-end">
              {RANGE_OPTIONS.map((option) => (
                <Button
                  key={option.id}
                  size="sm"
                  variant={activeRange === option.id ? 'primary' : 'outline-primary'}
                  onClick={() => setActiveRange(option.id)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </Col>
        </Row>
      </div>

      <Row className="g-3 mb-4">
        <Col md={4}>
          <Card className="controls-summary-card h-100">
            <Card.Body>
              <div className="controls-summary-value">{summary.active}</div>
              <div className="controls-summary-label">Active jobs in current range</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="controls-summary-card h-100">
            <Card.Body>
              <div className="controls-summary-value">{summary.completed}</div>
              <div className="controls-summary-label">Completed jobs in current range</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="controls-summary-card h-100">
            <Card.Body>
              <div className="controls-summary-value">{summary.unscheduled}</div>
              <div className="controls-summary-label">Jobs without software due date</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {visibleWeeks.length === 0 ? (
        <div className="alert alert-info">Calendar weeks are still loading.</div>
      ) : (
        <div className="table-responsive mb-4">
          <Table className="controls-calendar-table">
            <thead>
              <tr>
                <th>Sunday</th>
                <th>Monday</th>
                <th>Tuesday</th>
                <th>Wednesday</th>
                <th>Thursday</th>
                <th>Friday</th>
                <th>Saturday</th>
              </tr>
            </thead>
            <tbody>
              {visibleWeeks.map((week) => {
                const weekStart = moment(week.startDate);
                const days = Array.from({ length: 7 }, (_, index) => moment(weekStart).add(index, 'days'));

                return (
                  <tr key={`controls-week-${week.weekNum}`}>
                    {days.map((day) => {
                      const dayKey = day.format('YYYY-MM-DD');
                      const dayJobs = jobsByDate[dayKey] || [];

                      return (
                        <td key={dayKey}>
                          <div className="controls-calendar-day">
                            <div className="controls-calendar-date">{day.format('MMM D')}</div>
                            <div className="controls-calendar-items">
                              {dayJobs.map((job) => {
                                const statusLabel = getControlsStatusLabel(job);
                                const itemClassName = statusLabel === 'Complete' ? 'complete' : 'active';

                                return (
                                  <div
                                    key={`${job.ID}-${job.JobNumber}-${dayKey}`}
                                    className={`controls-calendar-item ${itemClassName}`}
                                    onDoubleClick={() => handleOpenModal(job)}
                                    role={canEdit ? 'button' : undefined}
                                    tabIndex={canEdit ? 0 : undefined}
                                    title={canEdit ? 'Double-click to edit job' : undefined}
                                  >
                                    <div className="controls-calendar-item-title">{job.JobNumber || 'Unknown job'}</div>
                                    <div className="controls-calendar-item-subtitle">{job.ProjectName || 'Untitled project'}</div>
                                    <div>Software Type: {job.SoftwareType || '-'}</div>
                                    <div className="mt-1">
                                      <Badge bg={statusLabel === 'Complete' ? 'success' : 'warning'}>{statusLabel}</Badge>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      <Card>
        <Card.Header>No Software Due Date</Card.Header>
        <Card.Body>
          {unscheduledJobs.length === 0 ? (
            <div className="text-muted">All visible controls jobs currently have a software due date.</div>
          ) : (
            <div className="controls-unscheduled-list">
              {unscheduledJobs.map((job) => (
                <div
                  key={`${job.ID}-${job.JobNumber}-unscheduled`}
                  className="controls-unscheduled-card"
                  onDoubleClick={() => handleOpenModal(job)}
                  role={canEdit ? 'button' : undefined}
                  tabIndex={canEdit ? 0 : undefined}
                  title={canEdit ? 'Double-click to edit job' : undefined}
                >
                  <div className="fw-bold">{job.JobNumber || 'Unknown job'}</div>
                  <div>{job.ProjectName || 'Untitled project'}</div>
                  <div className="mt-2">
                    <Badge bg={getControlsStatusLabel(job) === 'Complete' ? 'success' : 'warning'}>
                      {getControlsStatusLabel(job)}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {showModal && selectedJob && (
        <Modal
          show={showModal}
          onHide={createModalCloseHandler(handleCloseModal)}
          size="xl"
        >
          <EditJobModal
            selectedJob={selectedJob}
            setSelectedJob={setSelectedJob}
            handleCloseModal={handleCloseModal}
            handleSaveChanges={handleSaveChanges}
          />
        </Modal>
      )}
    </div>
  );
};

export default ControlsCalendar;